import React, { useState, useEffect } from 'react';
import './OrdersTable.css';

function OrdersTable({ orders, onRefreshOrders, emptyText = 'No claimed orders for this school yet.' }) {
  // Draft notes per order id (so typing doesn't fight the fetched data), and per-order save state.
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  // Per-order serial drill-in: which order is expanded, its fetched serials, and load state.
  const [expandedId, setExpandedId] = useState(null);
  const [serials, setSerials] = useState({}); // orderId -> OrderSerialDTO[]
  const [loadingSerialsId, setLoadingSerialsId] = useState(null);

  const [unclaimingId, setUnclaimingId] = useState(null);

  const handleUnclaim = async (order) => {
    if (unclaimingId) return;
    const ok = window.confirm(
      `Delete order ${order.number} from this account?\n\n` +
      `This moves the order back to UNCLAIMED so it can be claimed again. Its products are removed ` +
      `from the account and any student assignments to them are cleared. This can't be undone (but the ` +
      `order can be re-claimed).`
    );
    if (!ok) return;
    setUnclaimingId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/unclaim`, {
        method: 'POST',
        headers: { 'Authorization': 'Basic ' + btoa(`${username}:${password}`) }
      });
      if (response.ok) {
        const r = await response.json().catch(() => ({}));
        alert(`Order ${order.number} moved to unclaimed. ${r.bagsReleased ?? 0} product(s) released${r.studentsUnassigned ? `, ${r.studentsUnassigned} student assignment(s) cleared` : ''}.`);
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to delete order from account. Please try again.');
      }
    } catch (err) {
      console.error('Error unclaiming order:', err);
      alert('Error deleting order from account. Please try again.');
    } finally {
      setUnclaimingId(null);
    }
  };

  const toggleSerials = async (order) => {
    if (expandedId === order.id) { setExpandedId(null); return; }
    setExpandedId(order.id);
    if (serials[order.id]) return; // already loaded
    setLoadingSerialsId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/serials`, {
        headers: { 'Authorization': 'Basic ' + btoa(`${username}:${password}`) }
      });
      if (response.ok) {
        const data = await response.json();
        setSerials(prev => ({ ...prev, [order.id]: data }));
      } else {
        alert('Failed to load serial numbers.');
        setExpandedId(null);
      }
    } catch (err) {
      console.error('Error loading serials:', err);
      alert('Error loading serial numbers.');
      setExpandedId(null);
    } finally {
      setLoadingSerialsId(null);
    }
  };

  // Seed drafts from the loaded orders (and pick up refreshes) without clobbering active edits.
  useEffect(() => {
    setDrafts(prev => {
      const next = { ...prev };
      orders.forEach(o => {
        if (!(o.id in next)) next[o.id] = o.notes || '';
      });
      return next;
    });
  }, [orders]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const d = new Date(timestamp);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSaveNotes = async (order) => {
    if (savingId) return; // guard concurrent saves
    setSavingId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/notes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify({ notes: drafts[order.id] ?? '' })
      });

      if (response.ok) {
        alert('Notes saved');
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        alert('Failed to save notes. Please try again.');
      }
    } catch (err) {
      console.error('Error saving order notes:', err);
      alert('Error saving notes. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const totalUnits = (order) => order.products.reduce((sum, p) => sum + p.count, 0);

  if (!orders || orders.length === 0) {
    return (
      <div className="orders-table">
        <div className="no-orders">{emptyText}</div>
      </div>
    );
  }

  return (
    <div className="orders-table">
      <h3>Orders ({orders.length})</h3>
      <div className="orders-list">
        {orders.map(order => {
          const dirty = (drafts[order.id] ?? '') !== (order.notes || '');
          return (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <div className="order-title">
                  <button
                    className="order-number order-number-btn"
                    onClick={() => toggleSerials(order)}
                    title="Show serial numbers for this order"
                  >
                    Order {order.number}
                    <span className="order-number-caret">{expandedId === order.id ? '▾' : '▸'}</span>
                  </button>
                  <span className={`order-status status-${(order.status || '').toLowerCase()}`}>
                    {order.status}
                  </span>
                </div>
                <div className="order-meta">
                  {order.claimedBy && <span className="order-claimed-by">Claimed by {order.claimedBy}</span>}
                  <span>{formatDate(order.date)}</span>
                  <span>{totalUnits(order)} units</span>
                </div>
              </div>

              <div className="order-products">
                {order.products.length === 0 ? (
                  <div className="order-products-empty">No products linked to this order.</div>
                ) : (
                  order.products.map(p => (
                    <div key={p.model} className="order-product-row">
                      <span className="order-product-name">{p.displayName}</span>
                      <span className="order-product-code">{p.model}</span>
                      <span className="order-product-count">×{p.count}</span>
                    </div>
                  ))
                )}
              </div>

              {expandedId === order.id && (
                <div className="order-serials">
                  {loadingSerialsId === order.id ? (
                    <div className="order-serials-loading">Loading serial numbers…</div>
                  ) : (serials[order.id] || []).length === 0 ? (
                    <div className="order-serials-empty">No serial numbers linked to this order.</div>
                  ) : (
                    <table className="order-serials-table">
                      <thead>
                        <tr>
                          <th>Serial number</th>
                          <th>SKU</th>
                          <th>Product</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(serials[order.id] || []).map(s => (
                          <tr key={s.serialNumber}>
                            <td className="serial-mono">{s.serialNumber}</td>
                            <td className="serial-mono">{s.sku}</td>
                            <td>{s.displayName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              <div className="order-notes">
                <label className="order-notes-label">Notes (product iterations, batch info, internal)</label>
                <textarea
                  className="order-notes-input"
                  value={drafts[order.id] ?? ''}
                  onChange={(e) => setDrafts(prev => ({ ...prev, [order.id]: e.target.value }))}
                  placeholder="e.g. v2 hinge batch; shipped Mar 2026…"
                  rows={3}
                />
                <div className="order-notes-actions">
                  <button
                    className="order-notes-save"
                    onClick={() => handleSaveNotes(order)}
                    disabled={!dirty || savingId === order.id}
                  >
                    {savingId === order.id ? 'Saving…' : dirty ? 'Save notes' : 'Saved'}
                  </button>
                </div>
              </div>

              {(order.status || '').toLowerCase() === 'claimed' && (
                <div className="order-danger">
                  <button
                    className="order-unclaim-btn"
                    onClick={() => handleUnclaim(order)}
                    disabled={unclaimingId === order.id}
                    title="Move this order back to unclaimed so it can be claimed again"
                  >
                    {unclaimingId === order.id ? 'Removing…' : 'Delete order from account'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OrdersTable;
